var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "docs.markdown.engine",
  title: "Docs Markdown Engine",
  description: "Custom markdown parser and renderer for documentation pages."
});

var EMOJI = {
  smile: "😄",
  grin: "😁",
  joy: "😂",
  wink: "😉",
  heart: "❤️",
  thumbsup: "👍",
  thumbsdown: "👎",
  rocket: "🚀",
  fire: "🔥",
  warning: "⚠️",
  white_check_mark: "✅",
  x: "❌",
  info: "ℹ️",
  sparkles: "✨",
  star: "⭐",
  tada: "🎉",
  lock: "🔒",
  key: "🔑",
  book: "📘",
  memo: "📝"
};

function escapeHtml(text) {
  return (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .replace(/<[^>]*>/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
module.exports.slugify = slugify;

function parseScalar(value) {
  if (value === "true") return true;
  if (value === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    return Number(value);
  }

  var trimmed = value.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }

  return trimmed;
}

function parseFrontmatter(markdown) {
  var raw = markdown || "";
  if (!(raw.startsWith("---\n")) && !(raw.startsWith("---\r\n"))) {
    return {
      meta: {},
      body: raw
    };
  }

  var lines = raw.replace(/\r\n/g, "\n").split("\n");
  var endIndex = -1;
  for (var i = 1; i < lines.length; i++) {
    if (lines[i].trim() === "---") {
      endIndex = i;
      break;
    }
  }

  if (endIndex < 0) {
    return {
      meta: {},
      body: raw
    };
  }

  var meta = {};
  var currentListKey = "";

  for (var lineIndex = 1; lineIndex < endIndex; lineIndex++) {
    var line = lines[lineIndex];
    if (!(line.trim())) {
      continue;
    }

    var listMatch = line.match(/^\s*-\s+(.*)$/);
    if (listMatch && currentListKey) {
      if (!(Array.isArray(meta[currentListKey]))) {
        meta[currentListKey] = [];
      }
      meta[currentListKey].push(parseScalar(listMatch[1]));
      continue;
    }

    var match = line.match(/^\s*([A-Za-z0-9_.-]+)\s*:\s*(.*)$/);
    if (!(match)) {
      continue;
    }

    var key = match[1].trim();
    var value = match[2] || "";

    if (value.trim() === "") {
      meta[key] = [];
      currentListKey = key;
      continue;
    }

    meta[key] = parseScalar(value);
    currentListKey = "";
  }

  return {
    meta: meta,
    body: lines.slice(endIndex + 1).join("\n")
  };
}
module.exports.parseFrontmatter = parseFrontmatter;

function tokenizeInlineCode(text) {
  var store = [];
  var output = (text || "").replace(/`([^`]+)`/g, function(_, code) {
    var token = "\u0000DOCSINLINECODE" + store.length + "\u0000";
    store.push("<code>" + escapeHtml(code) + "</code>");
    return token;
  });

  return {
    text: output,
    restore: function(line) {
      var restored = line;
      for (var i = 0; i < store.length; i++) {
        var tokenPattern = new RegExp("\u0000DOCSINLINECODE" + i + "\u0000", "g");
        restored = restored.replace(tokenPattern, store[i]);
      }
      return restored;
    }
  };
}

function renderInline(text, options) {
  var tokenized = tokenizeInlineCode(text || "");
  var line = escapeHtml(tokenized.text);

  line = line.replace(/:([a-z0-9_+-]+):/gi, function(_, key) {
    var emoji = EMOJI[key.toLowerCase()];
    if (emoji) {
      return emoji;
    }
    return _;
  });

  line = line.replace(/!\[([^\]]*)\]\(([^\s)]+)(?:\s+"([^"]+)")?\)/g, function(_, alt, href, title) {
    var titleAttr = title ? (' title="' + escapeHtml(title) + '"') : "";
    return '<img src="' + escapeHtml(href) + '" alt="' + escapeHtml(alt) + '" loading="lazy"' + titleAttr + ' />';
  });

  line = line.replace(/\[([^\]]+)\]\(([^\s)]+)(?:\s+"([^"]+)")?\)/g, function(_, label, href, title) {
    if (href && options && typeof options.resolveHref === "function") {
      href = options.resolveHref(href);
    }
    var textLabel = label;
    if ((textLabel || "").trim() === (href || "").trim()) {
      textLabel = (href || "").replace(/^https?:\/\//i, "");
    }
    var titleAttr = title ? (' title="' + escapeHtml(title) + '"') : "";
    return '<a href="' + escapeHtml(href) + '"' + titleAttr + '>' + textLabel + "</a>";
  });

  line = line.replace(/&lt;(https?:\/\/[^&]+)&gt;/g, function(_, href) {
    var label = (href || "").replace(/^https?:\/\//i, "");
    return '<a href="' + escapeHtml(href) + '">' + escapeHtml(label || href) + "</a>";
  });
  line = line.replace(/&lt;([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})&gt;/g, '<a href="mailto:$1">$1</a>');

  // Convert any remaining plain URLs in text nodes to anchors so pages URL
  // transforms do not parse malformed URL suffixes like &quot;.
  line = linkifyBareUrlsOutsideTags(line);

  // Keep anchor labels human-friendly and prevent raw URL text from being
  // parsed again by downstream URL transformation.
  line = line.replace(/(<a\b[^>]*>)(https?:\/\/[^<]+)(<\/a>)/gi, function(_, open, hrefText, close) {
    return open + escapeHtml((hrefText || "").replace(/^https?:\/\//i, "")) + close;
  });

  line = line.replace(/\*\*\*([^*]+)\*\*\*/g, "<strong><em>$1</em></strong>");
  line = line.replace(/___([^_]+)___/g, "<strong><em>$1</em></strong>");
  line = line.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  line = line.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  line = line.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  line = line.replace(/_([^_]+)_/g, "<em>$1</em>");
  line = line.replace(/~~([^~]+)~~/g, "<del>$1</del>");

  return tokenized.restore(line);
}

function linkifyBareUrlsOutsideTags(htmlLine) {
  var chunks = (htmlLine || "").split(/(<[^>]+>)/g);
  for (var i = 0; i < chunks.length; i++) {
    var chunk = chunks[i];
    if (!(chunk) || chunk[0] === "<") {
      continue;
    }

    chunks[i] = chunk.replace(
      /\b(https?:\/\/[^\s<>"'&]*[^\s<>"'&.,;:!?])/gi,
      function(url) {
        var label = (url || "").replace(/^https?:\/\//i, "");
        return '<a href="' + escapeHtml(url) + '">' + escapeHtml(label || url) + "</a>";
      }
    );
  }

  return chunks.join("");
}

function neutralizeTextNodeProtocols(htmlLine) {
  var chunks = (htmlLine || "").split(/(<[^>]+>)/g);
  for (var i = 0; i < chunks.length; i++) {
    var chunk = chunks[i];
    if (!(chunk) || chunk[0] === "<") {
      continue;
    }

    chunks[i] = chunk.replace(/https?:\/\//gi, function(protocol) {
      var secure = protocol.toLowerCase().startsWith("https");
      return secure ? "https&#58;//" : "http&#58;//";
    });
  }

  return chunks.join("");
}

function unlinkQuotedAnchors(htmlLine) {
  return (htmlLine || "").replace(
    /(&quot;|")\s*<a\b[^>]*>([\s\S]*?)<\/a>\s*(&quot;|")/gi,
    function(_, openQuote, label, closeQuote) {
      return openQuote + label + closeQuote;
    }
  );
}

function extractPlainText(markdown) {
  var text = (markdown || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]+`/g, " ")
    .replace(/!\[[^\]]*\]\([^\)]*\)/g, " ")
    .replace(/\[[^\]]+\]\([^\)]*\)/g, "$1")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/[\*_~#>|\[\]`]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return text;
}
module.exports.extractPlainText = extractPlainText;

function splitTableLine(line) {
  var normalized = (line || "").trim();
  if (normalized.startsWith("|")) {
    normalized = normalized.slice(1);
  }
  if (normalized.endsWith("|")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized.split("|").map(function(item) {
    return item.trim();
  });
}

function isTableSeparator(line) {
  var cols = splitTableLine(line || "");
  if (cols.length === 0) {
    return false;
  }

  for (var i = 0; i < cols.length; i++) {
    if (!(cols[i].match(/^:?-{3,}:?$/))) {
      return false;
    }
  }

  return true;
}

async function renderFence(code, info, options) {
  var infoRaw = (info || "").trim();
  var infoParts = infoRaw ? infoRaw.split(/\s+/) : [];
  var lang = (infoParts[0] || "").toLowerCase();
  var hasPreview = infoParts.indexOf("preview") >= 0;

  var codeHtml = '<pre><code class="language-' + escapeHtml(lang || "text") + '">' + escapeHtml(code) + "</code></pre>";

  if (!(hasPreview)) {
    return '<section class="docs-code-block">' + codeHtml + "</section>";
  }

  var previewHtml = "";
  if (lang === "html") {
    previewHtml = '<div class="docs-preview-surface">' + code + "</div>";
  } else if (lang === "ua-json" || lang === "uajson" || lang === "uajson") {
    if (options && typeof options.renderUAJson === "function") {
      try {
        previewHtml = '<div class="docs-preview-surface">' + await options.renderUAJson(code) + "</div>";
      } catch (error) {
        previewHtml = '<div class="alert alert-warning mt-2">Unable to render UA/JSON preview: ' + escapeHtml(error.message || "Unknown error") + "</div>";
      }
    } else {
      previewHtml = '<div class="alert alert-warning mt-2">UA/JSON preview renderer is not available.</div>';
    }
  }

  if (!(previewHtml)) {
    return '<section class="docs-code-block">' + codeHtml + "</section>";
  }

  return '<section class="docs-code-preview">'
    + '<div class="docs-code-preview-source">' + codeHtml + "</div>"
    + '<div class="docs-code-preview-live">' + previewHtml + "</div>"
    + "</section>";
}

function isUnorderedMarker(value) {
  return value === "-" || value === "*" || value === "+";
}

async function renderList(lines, startIndex, options, headings) {
  var first = lines[startIndex];
  var firstMatch = first.match(/^(\s*)([*+-]|\d+\.)\s+(.*)$/);
  var ordered = !isUnorderedMarker(firstMatch[2]);
  var tag = ordered ? "ol" : "ul";
  var baseIndent = firstMatch[1].length;
  var i = startIndex;
  var html = "<" + tag + ' class="docs-list">';

  while (i < lines.length) {
    var line = lines[i];
    var match = line.match(/^(\s*)([*+-]|\d+\.)\s+(.*)$/);
    if (!(match)) {
      break;
    }

    var indent = match[1].length;
    var marker = match[2];
    var content = match[3] || "";

    if (indent < baseIndent) {
      break;
    }

    var lineOrdered = !isUnorderedMarker(marker);
    if (lineOrdered !== ordered && indent === baseIndent) {
      break;
    }

    var itemLines = [content];
    i += 1;

    while (i < lines.length) {
      var next = lines[i];
      if (!(next.trim())) {
        itemLines.push("");
        i += 1;
        continue;
      }

      var nextTrimmed = next.trim();
      if (nextTrimmed.match(/^\s{0,3}(#{1,6})\s+/)) break;
      if (nextTrimmed.match(/^\s{0,3}(```+|~~~+)/)) break;
      if (nextTrimmed.match(/^([-*_]\s*){3,}$/)) break;
      if (nextTrimmed.startsWith(">")) break;
      if (nextTrimmed.indexOf("|") >= 0 && i + 1 < lines.length && isTableSeparator(lines[i + 1])) break;

      var nextMatch = next.match(/^(\s*)([*+-]|\d+\.)\s+(.*)$/);
      if (nextMatch && nextMatch[1].length === indent) {
        break;
      }

      if (nextMatch && nextMatch[1].length < indent) {
        break;
      }

      if (next.length >= indent + 2) {
        itemLines.push(next.slice(indent + 2));
      } else {
        itemLines.push(next.trim());
      }
      i += 1;
    }

    var itemText = itemLines.join("\n").trim();
    var taskMatch = itemText.match(/^\[( |x|X)\]\s+([\s\S]*)$/);

    if (taskMatch) {
      var checked = taskMatch[1].toLowerCase() === "x";
      var bodyHtml = await renderMarkdownFragment(taskMatch[2], options, headings);
      html += '<li class="docs-task-item"><label><input type="checkbox" disabled'
        + (checked ? " checked" : "")
        + " />"
        + '<span class="docs-task-label">' + bodyHtml + "</span></label></li>";
      continue;
    }

    var rendered = await renderMarkdownFragment(itemText, options, headings);
    html += "<li>" + rendered + "</li>";
  }

  html += "</" + tag + ">";

  return {
    html: html,
    consumed: i - startIndex
  };
}

async function renderMarkdownFragment(markdown, options, headings) {
  var lines = (markdown || "").replace(/\r\n/g, "\n").split("\n");
  var html = [];

  for (var i = 0; i < lines.length;) {
    var line = lines[i];
    var trimmed = line.trim();

    if (!(trimmed)) {
      i += 1;
      continue;
    }

    var fence = line.match(/^\s{0,3}(```+|~~~+)(.*)$/);
    if (fence) {
      var fenceToken = fence[1];
      var fenceInfo = fence[2] || "";
      var codeLines = [];
      i += 1;

      while (i < lines.length) {
        if (lines[i].match(new RegExp("^\\s{0,3}" + fenceToken + "\\s*$"))) {
          i += 1;
          break;
        }
        codeLines.push(lines[i]);
        i += 1;
      }

      html.push(await renderFence(codeLines.join("\n"), fenceInfo, options));
      continue;
    }

    var atx = line.match(/^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+\s*)?$/);
    if (atx) {
      var level = atx[1].length;
      var title = atx[2].trim();
      var headingId = slugify(title);
      if (headings) {
        headings.push({
          id: headingId,
          level: level,
          text: title
        });
      }
      html.push("<h" + level + ' id="' + escapeHtml(headingId) + '">' + renderInline(title, options) + "</h" + level + ">");
      i += 1;
      continue;
    }

    if (i + 1 < lines.length) {
      var setext = lines[i + 1].match(/^\s{0,3}(=+|-+)\s*$/);
      if (setext && trimmed) {
        var setextLevel = setext[1][0] === "=" ? 1 : 2;
        var setextId = slugify(trimmed);
        if (headings) {
          headings.push({
            id: setextId,
            level: setextLevel,
            text: trimmed
          });
        }
        html.push("<h" + setextLevel + ' id="' + escapeHtml(setextId) + '">' + renderInline(trimmed, options) + "</h" + setextLevel + ">");
        i += 2;
        continue;
      }
    }

    if (trimmed.match(/^([-*_]\s*){3,}$/)) {
      html.push("<hr />");
      i += 1;
      continue;
    }

    if (trimmed.startsWith(">")) {
      var quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        quoteLines.push(lines[i].replace(/^\s{0,3}>\s?/, ""));
        i += 1;
      }
      var quoteHtml = await renderMarkdownFragment(quoteLines.join("\n"), options, headings);
      html.push("<blockquote>" + quoteHtml + "</blockquote>");
      continue;
    }

    if (line.match(/^(\s*)([*+-]|\d+\.)\s+/)) {
      var listResult = await renderList(lines, i, options, headings);
      html.push(listResult.html);
      i += listResult.consumed;
      continue;
    }

    if (trimmed.indexOf("|") >= 0 && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      var headerCols = splitTableLine(trimmed);
      var alignCols = splitTableLine(lines[i + 1]);
      var rows = [];
      i += 2;

      while (i < lines.length && lines[i].trim().indexOf("|") >= 0 && lines[i].trim() !== "") {
        rows.push(splitTableLine(lines[i]));
        i += 1;
      }

      var tableHtml = ["<div class=\"table-responsive\"><table class=\"table table-sm docs-table\">"];
      tableHtml.push("<thead><tr>");
      for (var h = 0; h < headerCols.length; h++) {
        var align = alignCols[h] || "";
        var alignAttr = "";
        if (align.startsWith(":") && align.endsWith(":")) alignAttr = ' style="text-align:center"';
        else if (align.endsWith(":")) alignAttr = ' style="text-align:right"';
        else if (align.startsWith(":")) alignAttr = ' style="text-align:left"';
        tableHtml.push("<th" + alignAttr + ">" + renderInline(headerCols[h], options) + "</th>");
      }
      tableHtml.push("</tr></thead><tbody>");
      for (var r = 0; r < rows.length; r++) {
        tableHtml.push("<tr>");
        for (var c = 0; c < headerCols.length; c++) {
          tableHtml.push("<td>" + renderInline(rows[r][c] || "", options) + "</td>");
        }
        tableHtml.push("</tr>");
      }
      tableHtml.push("</tbody></table></div>");
      html.push(tableHtml.join(""));
      continue;
    }

    if (trimmed.startsWith("<") && !(trimmed.startsWith("<!--"))) {
      var htmlBlock = [];
      while (i < lines.length && lines[i].trim() !== "") {
        htmlBlock.push(lines[i]);
        i += 1;
      }
      html.push(htmlBlock.join("\n"));
      continue;
    }

    var paragraph = [trimmed];
    i += 1;
    while (i < lines.length && lines[i].trim() !== "") {
      var nextTrim = lines[i].trim();
      if (nextTrim.match(/^\s{0,3}(#{1,6})\s+/)) break;
      if (nextTrim.match(/^\s{0,3}(```+|~~~+)/)) break;
      if (nextTrim.match(/^([-*_]\s*){3,}$/)) break;
      if (nextTrim.startsWith(">")) break;
      if (nextTrim.match(/^(\s*)([*+-]|\d+\.)\s+/)) break;
      if (nextTrim.indexOf("|") >= 0 && i + 1 < lines.length && isTableSeparator(lines[i + 1])) break;
      paragraph.push(nextTrim);
      i += 1;
    }

    html.push("<p>" + renderInline(paragraph.join(" "), options) + "</p>");
  }

  return html.join("\n");
}

async function renderMarkdown(markdown, options) {
  var front = parseFrontmatter(markdown || "");
  var headings = [];
  var html = await renderMarkdownFragment(front.body || "", options || {}, headings);
  html = unlinkQuotedAnchors(html);
  html = neutralizeTextNodeProtocols(html);

  var title = "";
  if (front.meta && front.meta.title && (front.meta.title + "").trim()) {
    title = (front.meta.title + "").trim();
  } else if (headings.length > 0) {
    title = headings[0].text;
  }

  var description = "";
  if (front.meta && front.meta.description && (front.meta.description + "").trim()) {
    description = (front.meta.description + "").trim();
  } else {
    description = extractPlainText(front.body).slice(0, 220);
  }

  return {
    meta: front.meta || {},
    markdown: front.body || "",
    html: html,
    headings: headings,
    title: title,
    description: description,
    text: extractPlainText(front.body || "")
  };
}
module.exports.renderMarkdown = renderMarkdown;
